import { useEffect, useState, useMemo } from "react";
import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { PageHeader } from "../../components/PageHeader";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useProfile } from "../../hooks/queries";
import { profileService } from "../../services/profileService";
import { recommendationsService } from "../../services/recommendationsService";  // eslint-disable-line no-unused-vars
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Brain, ArrowRight, AlertOctagon, AlertTriangle, Info,
  Wallet, CreditCard, TrendingUp, Target, ShieldAlert, CheckCircle2,
  TrendingDown, Sparkles, Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AdvisorChat } from "../../components/AdvisorChat";
import { Loader2 } from "lucide-react";
import { LockedBadge } from "../../components/LockedBadge";
import { LockedSection } from "../../components/LockedSection";
import { parseApiError } from "../../components/form-ux";
import { api } from "../../lib/api";
import { ResponsiveContainer, AreaChart, Area, Tooltip as RTooltip } from "recharts";

const RISK_OPTIONS = [
  { v: "conservative", en: "Conservative", es: "Conservador" },
  { v: "moderate", en: "Moderate", es: "Moderado" },
  { v: "aggressive", en: "Aggressive", es: "Agresivo" },
];
const HORIZON_OPTIONS = [
  { v: "short", en: "Short (<1y)", es: "Corto (<1a)" },
  { v: "medium", en: "Medium (1-5y)", es: "Medio (1-5a)" },
  { v: "long", en: "Long (>5y)", es: "Largo (>5a)" },
];
const GOAL_OPTIONS = [
  { v: "retirement", en: "Retirement", es: "Retiro" },
  { v: "emergency_fund", en: "Emergency fund", es: "Fondo de emergencia" },
  { v: "house", en: "House", es: "Vivienda" },
  { v: "education", en: "Education", es: "Educación" },
  { v: "travel", en: "Travel", es: "Viajes" },
  { v: "other", en: "Other", es: "Otro" },
];

const SparklineTooltip = ({ active, payload, lang }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-sky-200 rounded-md px-2 py-1 text-[11px] shadow-sm">
      <div className="font-semibold text-sky-700">
        W{p.iso_week} · {p.iso_year}
      </div>
      <div className="text-slate-700 tabular-nums">
        {p.count} {tx(lang, "active flags", "alertas activas", "alertas ativas")}
      </div>
    </div>
  );
};

// Wrapper for Recharts so the `content` prop is stable across
// renders (avoids react/no-unstable-nested-components warning).
function makeSparklineTooltip(lang) {
  const Wrapped = (props) => <SparklineTooltip {...props} lang={lang} />;
  Wrapped.displayName = "SparklineTooltipWrapped";
  return Wrapped;
}

const SEVERITY_STYLES = {
  high: {
    label: { en: "High", es: "Alta", pt: "Alta" },
    badge: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
    Icon: AlertOctagon,
    iconColor: "text-red-500",
    cardBg: "bg-red-50/50 border-red-200/70",
    sectionTint: "from-red-50 to-transparent",
  },
  medium: {
    label: { en: "Medium", es: "Media", pt: "Média" },
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    Icon: AlertTriangle,
    iconColor: "text-amber-500",
    cardBg: "bg-amber-50/40 border-amber-200/70",
    sectionTint: "from-amber-50 to-transparent",
  },
  low: {
    label: { en: "Low", es: "Baja", pt: "Baixa" },
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    Icon: Info,
    iconColor: "text-slate-400",
    cardBg: "bg-white border-slate-200",
    sectionTint: "from-slate-50 to-transparent",
  },
};

const CATEGORY_ICONS = {
  cash: Wallet,
  debt: CreditCard,
  investment: TrendingUp,
  goal: Target,
  risk: ShieldAlert,
};

const CATEGORY_LABELS = {
  cash: { en: "Cash", es: "Efectivo", pt: "Caixa" },
  debt: { en: "Debt", es: "Deuda", pt: "Dívida" },
  investment: { en: "Investment", es: "Inversión", pt: "Investimento" },
  goal: { en: "Goal", es: "Meta", pt: "Meta" },
  risk: { en: "Risk", es: "Riesgo", pt: "Risco" },
};

export default function Recommendations() {
  const { lang } = useLang();
  const { data: profile } = useProfile();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    age: "",
    risk_tolerance: "",
    investment_horizon: "",
    monthly_expenses: "",
    dependents: "",
    financial_goal_primary: "",
    fx_alert_threshold_pct: "",
  });
  // (Legacy one-shot advisor state removed; AdvisorChat owns the conversation now.)

  useEffect(() => {
    if (profile) {
      setForm({
        age: profile.age ?? "",
        risk_tolerance: profile.risk_tolerance ?? "",
        investment_horizon: profile.investment_horizon ?? "",
        monthly_expenses: profile.monthly_expenses ?? "",
        dependents: profile.dependents ?? "",
        financial_goal_primary: profile.financial_goal_primary ?? "",
        fx_alert_threshold_pct: profile.fx_alert_threshold_pct ?? "",
      });
    }
  }, [profile]);

  const recsQuery = useQuery({
    queryKey: ["recommendations"],
    queryFn: () => recommendationsService.list(),
  });
  const recs = recsQuery.data?.recommendations || [];
  const snapshot = recsQuery.data?.snapshot || {};
  const currency = recsQuery.data?.currency || profile?.currency || "USD";
  const trend = recsQuery.data?.trend || {};
  const resolvedSinceLast = trend.resolved || [];

  // iter-188 — Weekly trend sparkline. 8 weeks of "how many active
  // flags did I have each week". Encourages returning to the page to
  // see the curve descend.
  const historyQuery = useQuery({
    queryKey: ["recommendations-history"],
    queryFn: async () => (await api.get("/recommendations/history", { params: { weeks: 8 } })).data,
    staleTime: 5 * 60 * 1000,
  });
  const sparklineData = historyQuery.data?.series || [];
  // Compute delta vs last week so we can show a tiny arrow next to
  // the sparkline title ("you closed N flags this week" / "N new").
  const sparklineDelta = useMemo(() => {
    if (sparklineData.length < 2) return null;
    const last = sparklineData[sparklineData.length - 1]?.count ?? 0;
    const prev = sparklineData[sparklineData.length - 2]?.count ?? 0;
    return { last, prev, diff: last - prev };
  }, [sparklineData]);
  const sparklineTooltipComponent = useMemo(() => makeSparklineTooltip(lang), [lang]);

  // iter-186-ux — Group recommendations by severity so the user sees
  // critical items first. Counts power the summary chips at the top.
  const grouped = useMemo(() => {
    const buckets = { high: [], medium: [], low: [] };
    for (const r of recs) {
      (buckets[r.severity] || buckets.low).push(r);
    }
    return buckets;
  }, [recs]);
  const counts = {
    high: grouped.high.length,
    medium: grouped.medium.length,
    low: grouped.low.length,
  };

  const saveProfile = useMutation({
    mutationFn: (payload) => profileService.update(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["recommendations"] });
      toast.success(tx(lang, "Profile saved", "Perfil guardado", "Perfil guardado"));
    },
    onError: (e) => {
      // iter-169zze — show the backend's specific reason instead of
      // a generic "could not save". Useful when a server-side
      // validator rejects (e.g. age out of range) so the user sees why.
      const { message } = parseApiError(e);
      toast.error(message || (tx(lang, "Could not save", "No se pudo guardar", "No se pudo guardar")));
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      age: form.age ? Number(form.age) : null,
      risk_tolerance: form.risk_tolerance || null,
      investment_horizon: form.investment_horizon || null,
      monthly_expenses: form.monthly_expenses ? Number(form.monthly_expenses) : null,
      dependents: form.dependents !== "" ? Number(form.dependents) : null,
      financial_goal_primary: form.financial_goal_primary || null,
      fx_alert_threshold_pct: form.fx_alert_threshold_pct !== "" ? Number(form.fx_alert_threshold_pct) : null,
    };
    saveProfile.mutate(payload);
  };

  const resetProfile = useMutation({
    mutationFn: () => profileService.reset(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["recommendations"] });
      toast.success(tx(lang, "Optional fields cleared", "Campos opcionales borrados", "Campos opcionales borrados"));
    },
    onError: (e) => {
      const { message } = parseApiError(e);
      toast.error(message || (tx(lang, "Could not reset", "No se pudo resetear", "No se pudo resetear")));
    },
  });

  return (
    <Layout>
      <PageHeader
        eyebrow={
          <span
            data-testid="recs-scope-badge"
            className="inline-flex items-center gap-1.5"
          >
            <Brain className="h-3 w-3" />
            {tx(lang, "Profile + Recommendations", "Perfil + Recomendaciones", "Perfil + Recomendaciones")}
          </span>
        }
        title={tx(lang, "Recommendations", "Recomendaciones", "Recomendaciones")}
        subtitle={tx(lang, "Complete your financial profile. Lumfi generates immediate flags and on-demand can ask the AI advisor.", "Completa tu perfil financiero. Lumfi genera alertas inmediatas y bajo demanda puede pedirle consejo al asesor AI.", "Completa tu perfil financiero. Lumfi genera alertas inmediatas y bajo demanda puede pedirle consejo al asesor AI.")}
        testid="recommendations-header"
      />
      {/* Legacy h1 with the original testid preserved for older tests. The
          visible title now lives inside PageHeader. */}
      <h1 className="sr-only" data-testid="recommendations-title">
        {tx(lang, "Recommendations", "Recomendaciones", "Recomendaciones")}
      </h1>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Profile form */}
        <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-form-card">
          <h3 className="font-display text-lg font-bold mb-4">
            {tx(lang, "Your financial profile", "Tu perfil financiero", "Tu perfil financiero")}
          </h3>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{tx(lang, "Age", "Edad", "Edad")}</Label>
                <Input
                  data-testid="profile-age"
                  type="number" inputMode="decimal" autoComplete="off"
                  value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  placeholder="32"
                />
              </div>
              <div>
                <Label>{tx(lang, "Dependents", "Dependientes", "Dependientes")}</Label>
                <Input
                  data-testid="profile-dependents"
                  type="number" inputMode="decimal" autoComplete="off"
                  value={form.dependents}
                  onChange={(e) => setForm({ ...form, dependents: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Monthly expenses", "Gastos mensuales", "Gastos mensuales")}</Label>
                <Input
                  data-testid="profile-monthly-expenses"
                  type="number" inputMode="decimal" autoComplete="off"
                  value={form.monthly_expenses}
                  onChange={(e) => setForm({ ...form, monthly_expenses: e.target.value })}
                  placeholder={tx(lang, `Average in ${currency}`, `Promedio en ${currency}`, `Promedio en ${currency}`)}
                />
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Risk tolerance", "Tolerancia al riesgo", "Tolerancia al riesgo")}</Label>
                <Select value={form.risk_tolerance} onValueChange={(v) => setForm({ ...form, risk_tolerance: v })}>
                  <SelectTrigger data-testid="profile-risk"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {RISK_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{tx(lang, o.en, o.es, o.pt || o.es)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Investment horizon", "Horizonte de inversión", "Horizonte de inversión")}</Label>
                <Select value={form.investment_horizon} onValueChange={(v) => setForm({ ...form, investment_horizon: v })}>
                  <SelectTrigger data-testid="profile-horizon"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {HORIZON_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{tx(lang, o.en, o.es, o.pt || o.es)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Primary financial goal", "Objetivo financiero principal", "Objetivo financiero principal")}</Label>
                <Select value={form.financial_goal_primary} onValueChange={(v) => setForm({ ...form, financial_goal_primary: v })}>
                  <SelectTrigger data-testid="profile-goal"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {GOAL_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{tx(lang, o.en, o.es, o.pt || o.es)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>
                  {tx(lang, "FX drift alert threshold (%)", "Umbral de alerta FX drift (%)", "Umbral de alerta FX drift (%)")}
                  <LockedBadge featureKey="fx_alerts" className="ml-2" />
                </Label>
                <Input
                  data-testid="profile-fx-threshold"
                  type="number" inputMode="decimal" autoComplete="off"
                  step="0.1"
                  min="0"
                  value={form.fx_alert_threshold_pct}
                  onChange={(e) => setForm({ ...form, fx_alert_threshold_pct: e.target.value })}
                  placeholder={tx(lang, "Default 5% of cash", "Default 5% del efectivo", "Default 5% del efectivo")}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  {tx(lang, "Lumfi alerts via Telegram/WhatsApp when net FX drift exceeds this % of your cash.", "Lumfi alerta por Telegram/WhatsApp si la deriva FX neta supera este % de tu caja.", "Lumfi alerta por Telegram/WhatsApp si la deriva FX neta supera este % de tu caja.")}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                data-testid="profile-save"
                disabled={saveProfile.isPending}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl flex-1"
              >
                {saveProfile.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {tx(lang, "Save profile", "Guardar perfil", "Guardar perfil")}
              </Button>
              <Button
                type="button"
                data-testid="profile-reset"
                variant="outline"
                onClick={() => resetProfile.mutate()}
                disabled={resetProfile.isPending}
                className="rounded-xl"
                title={tx(lang, "Clear optional fields", "Borrar campos opcionales", "Borrar campos opcionales")}
              >
                {tx(lang, "Clear", "Limpiar", "Limpiar")}
              </Button>
            </div>
          </form>
        </Card>

        {/* Heuristic recommendations */}
        <Card className="p-6 rounded-2xl border-slate-200" data-testid="recs-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display text-lg font-bold">
                {tx(lang, "Heuristic flags", "Alertas heurísticas", "Alertas heurísticas")}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {tx(lang, "Instant rules over your current snapshot. No AI.", "Reglas instantáneas sobre tu instantánea actual. Sin AI.", "Reglas instantáneas sobre tu instantánea actual. Sin AI.")}
              </p>
            </div>
            <Badge variant="outline" className="text-xs">{recs.length}</Badge>
          </div>

          {/* iter-188 — 8-week activity sparkline. The curve trending
              down is the visible reward for resolving flags; trending
              up is the early-warning. */}
          {sparklineData.length > 0 && (
            <div
              data-testid="recs-sparkline"
              className="mb-4 p-3 rounded-xl border border-sky-200/70 bg-sky-50/30"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-sky-600" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-sky-700">
                    {tx(lang, "8-week activity", "Actividad de 8 semanas", "Atividade de 8 semanas")}
                  </span>
                </div>
                {sparklineDelta && sparklineDelta.diff !== 0 && (
                  <span
                    data-testid="recs-sparkline-delta"
                    className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${
                      sparklineDelta.diff > 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {sparklineDelta.diff > 0 ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {sparklineDelta.diff > 0
                      ? `+${sparklineDelta.diff} ${tx(lang, "this week", "esta semana", "esta semana")}`
                      : `${sparklineDelta.diff} ${tx(lang, "this week", "esta semana", "esta semana")}`}
                  </span>
                )}
              </div>
              <div className="h-12 -mx-1" data-testid="recs-sparkline-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <defs>
                      <linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.45} />
                        <stop offset="95%" stopColor="#7C3AED" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <RTooltip
                      cursor={{ stroke: "#A78BFA", strokeWidth: 1, strokeDasharray: "3 3" }}
                      content={sparklineTooltipComponent}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#7C3AED"
                      strokeWidth={1.75}
                      fill="url(#sparklineFill)"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between text-[9px] text-sky-600/70 mt-1">
                <span>{tx(lang, "8 weeks ago", "Hace 8 semanas", "8 semanas atrás")}</span>
                <span>{tx(lang, "This week", "Esta semana", "Esta semana")}</span>
              </div>
            </div>
          )}

          {/* iter-188 — Positive feedback: alerts that disappeared
              since the last snapshot. Encourages returning to the
              page weekly to see progress. */}
          {resolvedSinceLast.length > 0 && (
            <div
              data-testid="recs-resolved-banner"
              className="mb-4 p-3 rounded-xl border border-emerald-200 bg-emerald-50/60 flex items-start gap-2.5"
            >
              <Sparkles className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-xs text-emerald-900">
                <div className="font-semibold mb-0.5">
                  {tx(lang,
                    `${resolvedSinceLast.length} flag${resolvedSinceLast.length === 1 ? "" : "s"} resolved since your last visit`,
                    `${resolvedSinceLast.length} alerta${resolvedSinceLast.length === 1 ? "" : "s"} resuelta${resolvedSinceLast.length === 1 ? "" : "s"} desde tu última visita`,
                    `${resolvedSinceLast.length} alerta${resolvedSinceLast.length === 1 ? "" : "s"} resolvida${resolvedSinceLast.length === 1 ? "" : "s"} desde sua última visita`)}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {resolvedSinceLast.map((r) => (
                    <span
                      key={r.id}
                      data-testid={`recs-resolved-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[11px] font-medium"
                    >
                      <TrendingDown className="h-3 w-3" /> {r.title}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Severity summary chips */}
          {recs.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="recs-summary">
              {(["high", "medium", "low"]).map((sev) => {
                if (counts[sev] === 0) return null;
                const s = SEVERITY_STYLES[sev];
                const SIcon = s.Icon;
                return (
                  <div
                    key={sev}
                    data-testid={`recs-summary-${sev}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${s.badge}`}
                  >
                    <SIcon className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{counts[sev]}</span>
                    <span className="capitalize">{tx(lang, s.label.en, s.label.es, s.label.pt)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {recsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {tx(lang, "Loading…", "Cargando…", "Carregando…")}
            </div>
          ) : recs.length === 0 ? (
            <div
              className="flex flex-col items-center text-center py-8 px-4 rounded-xl bg-emerald-50/60 border border-emerald-200/60"
              data-testid="recs-empty"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-2" />
              <div className="font-semibold text-emerald-900 text-sm">
                {tx(lang, "All clear", "Todo en orden", "Tudo em ordem")}
              </div>
              <p className="text-xs text-emerald-800/80 mt-1 max-w-xs">
                {tx(lang, "No flags right now. If you're just starting, fill out your profile to activate more rules.", "Sin alertas por ahora. Si recién empiezas, completa tu perfil para activar más reglas.", "Sem alertas no momento. Se está começando, complete seu perfil para ativar mais regras.")}
              </p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="recs-list">
              {(["high", "medium", "low"]).map((sev) => {
                const items = grouped[sev];
                if (!items || items.length === 0) return null;
                const s = SEVERITY_STYLES[sev];
                const SIcon = s.Icon;
                return (
                  <section key={sev} data-testid={`recs-section-${sev}`}>
                    <div className="flex items-center gap-2 mb-2.5">
                      <SIcon className={`h-4 w-4 ${s.iconColor}`} />
                      <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                        {tx(lang, s.label.en, s.label.es, s.label.pt)}
                      </h4>
                      <span className="text-[11px] text-slate-400 tabular-nums">· {items.length}</span>
                    </div>
                    <ul className="space-y-2">
                      {items.map((r) => {
                        const CatIcon = CATEGORY_ICONS[r.category] || ShieldAlert;
                        const catLabel = CATEGORY_LABELS[r.category];
                        return (
                          <li
                            key={r.id}
                            data-testid={`rec-${r.id}`}
                            className={`p-3.5 rounded-xl border ${s.cardBg} transition-all hover:shadow-sm`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`shrink-0 h-9 w-9 rounded-lg bg-white border ${s.cardBg.includes("red") ? "border-red-200" : s.cardBg.includes("amber") ? "border-amber-200" : "border-slate-200"} flex items-center justify-center`}>
                                <CatIcon className={`h-4 w-4 ${s.iconColor}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-semibold text-slate-900 text-sm leading-snug">{r.title}</span>
                                    {r.trend === "new" && trend.compared_to && (
                                      <span
                                        data-testid={`rec-trend-new-${r.id}`}
                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider"
                                        title={tx(lang, "New since your last visit", "Nueva desde tu última visita", "Nova desde sua última visita")}
                                      >
                                        <TrendingUp className="h-2.5 w-2.5" />
                                        {tx(lang, "New", "Nueva", "Nova")}
                                      </span>
                                    )}
                                  </div>
                                  {catLabel && (
                                    <Badge variant="outline" className="text-[9px] uppercase shrink-0 bg-white">
                                      {tx(lang, catLabel.en, catLabel.es, catLabel.pt)}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{r.rationale}</p>
                                {r.action && (
                                  <Link
                                    to={r.action}
                                    data-testid={`rec-action-${r.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 mt-2.5 px-2.5 py-1 rounded-md bg-white border border-emerald-200/70 hover:border-emerald-300 transition-colors"
                                  >
                                    {tx(lang, "Take action", "Resolver ahora", "Resolver agora")} <ArrowRight className="h-3 w-3" />
                                  </Link>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* AI Advisor — persistent conversation (gated by ai_recommendations).
          iter-170ad — Migrated from FeatureGate to LockedSection so the
          upsell shows the plan/addon/pack picker. */}
      <LockedSection
        featureKey="ai_recommendations"
        titleEs="Recomendaciones IA"
        titleEn="AI recommendations"
        titlePt="Recomendações IA"
        bullets={[
          { es: "Consejos personalizados generados con tu perfil financiero real",
            en: "Personalised advice generated from your real financial profile",
            pt: "Conselhos personalizados com seu perfil financeiro real" },
          { es: "Acciones ejecutables sugeridas (no solo lectura)",
            en: "Executable suggested actions (not just reading)",
            pt: "Ações sugeridas executáveis" },
          { es: "Re-evalúa cada vez que lo pides",
            en: "Re-runs whenever you ask",
            pt: "Reavalia toda vez que você pede" },
        ]}
      >
        <AdvisorChat />
      </LockedSection>

      {/* Snapshot footer */}
      {snapshot && Object.keys(snapshot).length > 0 && (
        <Card className="mt-5 p-4 rounded-2xl border-slate-200 text-xs" data-testid="recs-snapshot">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
            {tx(lang, "Snapshot used", "Instantánea usada", "Instantánea usada")}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-slate-600 tabular-nums">
            <span>Cash: {snapshot.cash}</span>
            <span>{tx(lang, "Monthly debt:", "Pagos mensuales:", "Pagos mensuales:")} {snapshot.monthly_debt_payments}</span>
            <span>{tx(lang, "Total debt:", "Deuda total:", "Deuda total:")} {snapshot.debt_total}</span>
            <span>{tx(lang, "Total assets:", "Activos totales:", "Activos totales:")} {snapshot.total_assets}</span>
            <span>{tx(lang, "Investments:", "Inversiones:", "Inversiones:")} {snapshot.investments_count}</span>
            <span>{tx(lang, "Goals:", "Metas:", "Metas:")} {snapshot.goals_count}</span>
          </div>
        </Card>
      )}
    </Layout>
  );
}
